/**
 * Web storage that never throws.
 *
 * Safari private mode, some enterprise browser policies and a few embedded
 * webviews throw on the ACCESSOR -- `window.localStorage` itself -- not just on
 * a write over quota. An unguarded read in a render path took down every
 * listing page in Safari private once, and the analytics tracker's own reads
 * did the same to every page that mounts analytics. This is the one place that
 * knows how to touch storage; callers get a value or a fallback, never an
 * exception.
 *
 * What lives in web storage is, by policy, a per-browser convenience: a
 * remembered dismissal, an in-session tally, a cached id. Nothing here is a
 * system of record, so silently degrading to "not stored" is the correct
 * failure mode -- the page renders as it would for a first-time visitor.
 */

export type StorageArea = 'local' | 'session';

function area(kind: StorageArea): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    const store = kind === 'local' ? window.localStorage : window.sessionStorage;
    return store ?? null;
  } catch {
    return null;
  }
}

/** Raw string read. `null` when missing, blocked or unavailable. */
export function readString(kind: StorageArea, key: string): string | null {
  try {
    return area(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** Raw string write. Returns whether it stuck. */
export function writeString(kind: StorageArea, key: string, value: string): boolean {
  try {
    const store = area(kind);
    if (!store) return false;
    store.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function removeKey(kind: StorageArea, key: string): boolean {
  try {
    const store = area(kind);
    if (!store) return false;
    store.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export type ReadJSONFailure =
  /** Nothing stored under the key (or storage unavailable). */
  | { ok: false; reason: 'missing' }
  /** Stored, but not JSON. `error` is what JSON.parse threw. */
  | { ok: false; reason: 'corrupt'; error: unknown }
  /** Parsed, but not the shape the caller insisted on. */
  | { ok: false; reason: 'invalid'; value: unknown };

export type ReadJSONResult<T> = { ok: true; value: T } | ReadJSONFailure;

const isPresent = (value: unknown): value is unknown => value !== null && value !== undefined;

/**
 * JSON read that says WHY it failed. Most callers want `readJSON` and a
 * fallback; this exists for the few that report a corrupt value -- a stored
 * value that no longer parses means some build wrote something it should not
 * have, and that is worth a warning even though the page carries on.
 */
export function readJSONResult<T>(
  kind: StorageArea,
  key: string,
  isValid: (value: unknown) => value is T = isPresent as (value: unknown) => value is T
): ReadJSONResult<T> {
  const raw = readString(kind, key);
  if (raw === null || raw === '') return { ok: false, reason: 'missing' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return { ok: false, reason: 'corrupt', error };
  }
  return isValid(parsed) ? { ok: true, value: parsed } : { ok: false, reason: 'invalid', value: parsed };
}

/**
 * JSON read with a fallback. A missing key, blocked storage, a corrupt value or
 * a value of the wrong shape all yield the fallback -- a stale or hand-edited
 * entry must never be able to break a page that only kept it as a convenience.
 *
 * `isValid` lets a caller insist on a shape (an object, an array) without
 * re-implementing the try/catch at every call site.
 */
export function readJSON<T>(
  kind: StorageArea,
  key: string,
  fallback: T,
  isValid: (value: unknown) => value is T = isPresent as (value: unknown) => value is T
): T {
  const result = readJSONResult<T>(kind, key, isValid);
  return result.ok ? result.value : fallback;
}

/** JSON write. Returns whether it stuck. */
export function writeJSON(kind: StorageArea, key: string, value: unknown): boolean {
  try {
    return writeString(kind, key, JSON.stringify(value));
  } catch {
    // JSON.stringify throws on cycles and BigInt; treat as "did not store".
    return false;
  }
}

/** A plain object guard for `readJSON`, the shape most stored records take. */
export function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
